import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeysClient } from 'common/clients/query-key.client';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import {
  createAccountService,
  createBranchAccountService,
  customerAccountCreateService,
  getAdminAccountsListService,
  getCounterpartyAccountDetailsService,
  getCounterpartyAccountsService,
  getCustomerAccountsDetailsService,
} from '../services/accounts.service';
import {
  TAccountDetailRes,
  TAccountsListReqParams,
  TAccountsListResData,
  TAccountCustomersResData,
  TCreateCustomerAccountReqParams,
  TAdminAccountsReqParams,
  TCreateAccountReqParams,
  TCreateBranchAccountReqParams,
} from '../types/accounts.types';

export const useCounterpartyAccountsListQuery = (
  counterpartyGUID: string,
  params: TAccountsListReqParams = {},
  options?: any,
): UseQueryResult<TAccountsListResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccounts(counterpartyGUID, params),
    ...options,
  });

export const useCounterpartyBranchAccountsListQuery = (
  counterpartyGUID: string,
  branchGUID: string,
  params: TAccountsListReqParams = {},
  options?: any,
): UseQueryResult<TAccountsListResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyBranchAccounts(counterpartyGUID, branchGUID, params),
    ...options,
  });

export const useCounterpartyAccountDetailsQuery = (
  counterpartyGUID: string,
  accountGUID: string,
  params: TAccountsListReqParams = {},
  options?: any,
): UseQueryResult<TAccountDetailRes> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccountDetails(
      counterpartyGUID,
      accountGUID,
      params,
    ),
    ...options,
  });

export const useCustomerAccountsListQuery = (
  counterpartyGUID: string,
  customerGUID: string,
  params: TAccountsListReqParams = {},
  options?: any,
): UseQueryResult<TAccountCustomersResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.AccountQueryKeys].getAccountsList(counterpartyGUID, customerGUID, params),
    ...options,
  });

export const useCustomerAccountsListMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; customerGUID: string; params?: TAccountsListReqParams }) =>
      getCustomerAccountsDetailsService(data.counterpartyGUID, data.customerGUID),
  });

export const useCreateCustomerAccountMutation = (): any =>
  useMutation({
    mutationFn: (data: {
      counterpartyGUID: string;
      customerGUID: string;
      iban?: string;
      params?: TCreateCustomerAccountReqParams;
    }) => customerAccountCreateService(data.counterpartyGUID, data.customerGUID, data.iban),
  });


export const useCreateAccountMutation = (): any =>
  useMutation({
    mutationFn: (data: {
      counterpartyGUID: string;
      requestData: TCreateAccountReqParams;
    }) => createAccountService(data.counterpartyGUID, data.requestData),
  });

export const useCreateBranchAccountMutation = (): any =>
  useMutation({
    mutationFn: (data: {
      counterpartyGUID: string;
      branchGUID: string;
      requestData: TCreateBranchAccountReqParams;
    }) => createBranchAccountService(data.counterpartyGUID, data.branchGUID, data.requestData),
  });

export const useAdminAccountsListMutation = (): any =>
  useMutation({
    mutationFn: (data: { params?: TAdminAccountsReqParams }) =>
      getAdminAccountsListService(data?.params),
  });

export const useAccountsListMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string, params?: TAccountsListReqParams }) =>
      getCounterpartyAccountsService(data.counterpartyGUID, data?.params),
  });

export const useGetAccountDetailsMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string, accountGUID: string }) =>
      getCounterpartyAccountDetailsService(data.counterpartyGUID, data.accountGUID),
  });
